import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import moment from 'moment-timezone'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector, useStore } from 'react-redux'
import {
  selectCreditCardListModelFromState,
  selectGetVirtualCardFailed,
  selectMyWalletVirtualCard,
  selectMyWalletVirtualCardIdFromRoute,
  selectMyWalletVirtualCardPermissions,
  selectVirtualCard,
  selectVirtualCardIdFromRoute,
  selectVirtualCardPermissions,
} from 'src/app/VirtualCardDetail/redux/virtualCardDetail.selectors'
import ActiveUntilBox from 'src/components/ActiveUntilBox/ActiveUntilBox'
import ActivityBox from 'src/components/ActivityBox/ActivityBox'
import AdditionalVirtualCardDetailsBox from 'src/components/AdditionalVirtualCardDetailsBox/AdditionalVirtualCardDetailsBox'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import Box from 'src/components/Box/Box'
import HorizontalContentCollection from 'src/components/HorizontalContentCollection/HorizontalContentCollection'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotesBox from 'src/components/NotesBox/NotesBox'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import PendingModificationBox from 'src/components/PendingModificationBox/PendingModificationBox'
import RecurringBox from 'src/components/RecurringBox/RecurringBox'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import VerticalContentCollection from 'src/components/VerticalContentCollection/VerticalContentCollection'
import VirtualCardAccountingBox from 'src/components/VirtualCardAccountingBox/VirtualCardAccountingBox'
import VirtualCardDetailHeader from 'src/components/VirtualCardDetailHeader/VirtualCardDetailHeader'
import VirtualCardDetailTopnav from 'src/components/VirtualCardDetailTopnav/VirtualCardDetailTopnav'
import { useBackgroundDispatch } from 'src/hooks'
import { useGoogleWalletProvisioning } from 'src/hooks/useGoogleWalletProvisioning'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { TransactionStatus } from 'src/models/transaction.models'
import {
  ApprovalStatus,
  ScreeningStatus,
  WalletType,
} from 'src/models/virtualCard.models'
import { connectedReplace, Rdrct } from 'src/navigation/Lnk'
import {
  billPayDetailUrl,
  manageOrgInviteUrl,
  screenVirtualCardUrl,
  updateVirtualCardUrl,
  userScreeningInfoUrl,
} from 'src/navigation/urls'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useQueryUser } from 'src/queries/users.queries'
import { getCreditCard } from 'src/redux/actions/data/creditCards.actions'
import { getOrganizationInvites } from 'src/redux/actions/data/organizations.actions'
import {
  getVirtualCard,
  getVirtualCardPermissions,
  getVirtualCardsStatisticsClearedTransactionCreditAmount,
  getVirtualCardsStatisticsClearedTransactionDebitAmount,
} from 'src/redux/actions/data/virtualCards.actions'
import {
  selectUserIdFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import {
  selectCardApprovalsFromState,
  selectOrganizationInvitesListResponsesFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectPushToWalletWhitelistFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectVirtualCardClearedTransactionAmountFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import {
  selectApiHostFromState,
  selectAppName,
  selectEnvConfigFromState,
} from 'src/redux/selectors/session.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

interface WPPResult {
  status: string
  statusMessage: string
  state: string
}

export enum VirtualCardDetailContext {
  myWallet = 'myWallet',
  cards = 'cards',
}

interface VirtualCardDetailProps {
  context?: VirtualCardDetailContext
}

const VirtualCardDetail: React.FC<VirtualCardDetailProps> = (props) => {
  const context =
    props.context !== undefined ? props.context : VirtualCardDetailContext.cards

  const dispatch = useBackgroundDispatch()
  const queryClient = useQueryClient()
  const store = useStore()

  const virtualCardId =
    context === VirtualCardDetailContext.myWallet
      ? useSelector(selectMyWalletVirtualCardIdFromRoute)
      : useSelector(selectVirtualCardIdFromRoute)

  const virtualCard =
    props.context === VirtualCardDetailContext.myWallet
      ? useSelector(selectMyWalletVirtualCard)
      : useSelector(selectVirtualCard)

  const virtualCardPermissions =
    context === VirtualCardDetailContext.myWallet
      ? useSelector(selectMyWalletVirtualCardPermissions)
      : useSelector(selectVirtualCardPermissions)

  const { data: sender } = useQueryUser({ userId: virtualCard?.cardholderId })
  const { data: recipient } = useQueryUser({ userId: virtualCard?.recipientId })
  const userId = useSelector(selectUserIdFromState)
  const user = useSelector(selectUserListModelFromState)
  const getVirtualCardFailed = useSelector(selectGetVirtualCardFailed)
  const vcClearedTransactionAmountFromState = useSelector(
    selectVirtualCardClearedTransactionAmountFromState,
  )
  const orgInvites = useSelector(
    selectOrganizationInvitesListResponsesFromState,
  )
  const orgInvite = useMemo(() => {
    return Object.values(orgInvites).filter(
      (orgInvite) => orgInvite.inviter.id === virtualCard?.cardholderId,
    )
  }, [orgInvites])
  const appName = useSelector(selectAppName)
  const creditCard = useSelector(selectCreditCardListModelFromState)
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)
  const apiHost = useSelector(selectApiHostFromState)
  const pushToWalletWhitelist = useSelector(
    selectPushToWalletWhitelistFromRemoteConfigState,
  )
  const addToWalletButtonId = 'add-to-apple-wallet'

  const envConfig = useSelector(selectEnvConfigFromState)

  const isPendingFinalApproval =
    virtualCard?.awaitingApprovalStatus ===
      ApprovalStatus.AwaitingApproverRuleApproval &&
    cardApprovalRules?.approverId !== undefined

  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const hasIntegrations = activeIntegration !== undefined

  const isGuestCard = _.get(virtualCard, 'direct', false)

  const hideCardDetails =
    !user?.isInExplicitOrg &&
    user?.organizationId !== sender?.organizationId &&
    !isGuestCard
  const showOrgInviteModal = hideCardDetails && orgInvite.length > 0

  const appleAddToWalletEnabled =
    virtualCard?.features.walletsEnabled !== undefined &&
    virtualCard.features.walletsEnabled.includes(WalletType.ApplePay)
  const currentUserIsRecipient =
    virtualCard !== undefined &&
    userId !== undefined &&
    virtualCard.recipientId === userId
  const walletRecipientIsWhitelisted =
    userId !== undefined && pushToWalletWhitelist?.includes(userId)
  const walletRecipientIsEligible =
    virtualCard !== undefined &&
    currentUserIsRecipient &&
    walletRecipientIsWhitelisted
  const showAppleAddToWallet =
    appleAddToWalletEnabled && walletRecipientIsEligible
  const { googleWalletAdded, showGoogleAddToWallet, onGoogleAddToWalletClick } =
    useGoogleWalletProvisioning({
      virtualCard,
      recipient,
      currentUserIsRecipient,
      walletRecipientIsWhitelisted,
    })
  const showPushToWallet = showAppleAddToWallet || showGoogleAddToWallet

  const virtualCardTransactionFilters: TransactionsFiltersBody = {
    virtualCardId: [virtualCardId],
    status: [
      TransactionStatus.Cleared,
      TransactionStatus.Declined,
      TransactionStatus.Pending,
      TransactionStatus.AuthReversal,
    ],
    sort: '-dateAuthsFirst',
  }

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 76)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 76)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatch(getVirtualCard(virtualCardId))
    dispatch(getVirtualCardPermissions(virtualCardId))
    dispatch(
      getVirtualCardsStatisticsClearedTransactionDebitAmount([virtualCardId]),
    )
    dispatch(
      getVirtualCardsStatisticsClearedTransactionCreditAmount([virtualCardId]),
    )
  }, [virtualCard?.status, virtualCardId])

  useEffect((): void => {
    if (virtualCard?.creditCardId !== undefined) {
      dispatch(getCreditCard(virtualCard?.creditCardId))
    }
    // refetch all visible transactions in case expense categories or vendor updated
    queryClient.invalidateQueries({ queryKey: transactionsQueryKeys.all })
  }, [virtualCard])

  useEffect(() => {
    if (virtualCard?.screeningRequired && virtualCard.isActive) {
      if (virtualCard.screening?.status === ScreeningStatus.NeedsMoreInfo) {
        dispatch(connectedReplace(screenVirtualCardUrl(virtualCard.id)))
      } else if (virtualCard.screening?.status === ScreeningStatus.Forbidden) {
        dispatch(
          connectedReplace({
            pathname: userScreeningInfoUrl(),
            search: `?status=${ScreeningStatus.Forbidden}`,
            state: {},
            hash: '',
          }),
        )
      } else if (virtualCard.screening?.status === ScreeningStatus.Fail) {
        dispatch(
          connectedReplace({
            pathname: userScreeningInfoUrl(),
            search: `?status=${ScreeningStatus.Fail}`,
            state: {},
            hash: '',
          }),
        )
      } else if (
        virtualCard.screening?.status === ScreeningStatus.ServiceUnavailable
      ) {
        dispatch(
          connectedReplace({
            pathname: userScreeningInfoUrl(),
            search: `?status=${ScreeningStatus.ServiceUnavailable}`,
            state: {},
            hash: '',
          }),
        )
      }
    }
  }, [virtualCard?.screeningRequired, virtualCard?.screening?.status])

  // get org invites on render
  useEffect(() => {
    dispatch(getOrganizationInvites())
  }, [])

  useEffect(() => {
    if (
      userId !== undefined &&
      showAppleAddToWallet &&
      virtualCard?.id !== undefined
    ) {
      setTimeout(() => {
        initAddToAppleWallet(userId, virtualCard.id)
      }, 2000)
    }
  }, [showAppleAddToWallet, userId, virtualCard])

  // redirect to org invite if user has pending org invite and is the same org
  useEffect((): void => {
    if (showOrgInviteModal) {
      dispatch(connectedReplace(manageOrgInviteUrl(orgInvite[0]?.id)))
    }
  }, [orgInvite])

  const descriptor = isGuestCard
    ? `${recipient?.firstNameOrEmail}'s Guest Virtual Card`
    : `${recipient?.firstNameOrEmail}'s Virtual Card`

  const jwsResolverCallback = (
    userId: string,
    virtualCardId: string,
  ): (() => Promise<unknown>) => {
    return () =>
      new Promise((resolve, reject) => {
        fetch(`${apiHost}/push`, {
          method: 'POST',
          body: JSON.stringify({
            userId: userId,
            virtualCardId: virtualCardId,
          }),
          headers: selectAuthedJsonApiRequestConfig(store.getState()).headers,
        })
          .then((response) => response.json())
          .then((json) => {
            resolve(json)
          })
          .catch((error) => reject(error))
      })
  }

  const resultResolverCallback = (result: WPPResult): void => {
    // TODO: add more cases here to test
    if (result.status === '500') {
      // Action for error
    } else if (result.status === '408') {
      // Action for timeout
    } // Other cases continues
  }

  const initAddToAppleWallet = (
    userId: string,
    virtualCardId: string,
  ): void => {
    const initialized = document
      .getElementById(addToWalletButtonId)
      ?.getAttribute('data-initialized')
    if (!initialized) {
      window.initAddToAppleWallet({
        partnerId: envConfig.APPLE_PAY_PARTNER_ID,
        domain: envConfig.APPLE_PAY_URL,
        buttonId: addToWalletButtonId,
        jwtResolver: jwsResolverCallback(userId, virtualCardId),
        resultResolver: resultResolverCallback,
      })
    }
  }

  return virtualCard !== undefined && userId !== undefined ? (
    <BackgroundPage>
      {virtualCard.isBillPay && <Rdrct to={billPayDetailUrl(virtualCard.id)} />}
      <Helmet>
        <title>{`${descriptor} | ${appName}`}</title>
        <meta name="description" content={`${descriptor} page of ${appName}`} />
      </Helmet>
      <VirtualCardDetailTopnav
        virtualCard={virtualCard}
        virtualCardPermissions={virtualCardPermissions}
        recipient={recipient}
        recipientEmail={recipient?.email}
      />
      <VirtualCardDetailHeader
        virtualCard={virtualCard}
        virtualCardPermissions={virtualCardPermissions}
        recipient={recipient}
        hideCardDetails={hideCardDetails}
        pendingFinalApproval={isPendingFinalApproval}
        showPushToWallet={showPushToWallet}
        showAppleAddToWallet={showAppleAddToWallet}
        showGoogleAddToWallet={showGoogleAddToWallet}
        googleWalletAdded={googleWalletAdded}
        addToWalletButtonId={addToWalletButtonId}
        onGoogleAddToWalletClick={onGoogleAddToWalletClick}
      />
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent>
          {virtualCard.pending !== undefined && (
            <PendingModificationBox
              virtualCard={virtualCard}
              virtualCardPermissions={virtualCardPermissions}
              timezone={virtualCard.timezone}
              pendingRevision={virtualCard.pending}
            />
          )}
          <HorizontalContentCollection>
            <VerticalContentCollection
              css={css`
                z-index: 1;
              `}
            >
              {virtualCard.recurrence !== undefined && (
                <RecurringBox
                  isInactive={virtualCard.isInactive}
                  timezone={virtualCard.timezone}
                  recurrence={virtualCard.recurrence}
                  openLeftMdUp={false}
                  openLeftSmDown
                  currency={virtualCard.currency}
                />
              )}
              {!isGuestCard && virtualCard.isIssuerCycle && (
                <Box
                  data-testid="issuerCycleCardInformation"
                  css={css`
                    display: flex;
                    flex-direction: row;
                    gap: 40px;
                    padding: 40px;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <span
                      css={css`
                        ${getFontCss(
                          14,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns source={'Current Credit Limit'} />
                    </span>
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-top: 4px;
                      `}
                      data-testid="currentCreditLimit"
                    >
                      <Crncy
                        currency={virtualCard.currency}
                        amount={toDollars(virtualCard.limitCents)}
                        maximumFractionDigits={0}
                      />
                    </span>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <span
                      css={css`
                        ${getFontCss(
                          14,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      <Trns source={'Next Cycle Date'} />
                    </span>
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-top: 4px;
                      `}
                      data-testid="nextCycleDate"
                    >
                      {virtualCard.issuerCycleConfig?.nextAt
                        ? moment(virtualCard.issuerCycleConfig.nextAt).format(
                            'll',
                          )
                        : '--'}
                    </span>
                  </div>
                </Box>
              )}
              {!isGuestCard && sender && recipient && (
                <AdditionalVirtualCardDetailsBox
                  virtualCard={virtualCard}
                  virtualCardPermissions={virtualCardPermissions}
                  sender={sender}
                  recipient={recipient}
                  creditCard={creditCard}
                  pendingFinalApproval={isPendingFinalApproval}
                />
              )}
              <ActiveUntilBox
                inactiveSinceString={virtualCard.inactiveSinceTimezonedString}
                activeUntilString={virtualCard.activeUntilTimezonedString}
                pendingRecurringTo={
                  virtualCard.isPending && virtualCard.recurs
                    ? updateVirtualCardUrl(virtualCard.id)
                    : undefined
                }
                openLeftMdUp={false}
                openLeftSmDown
                consumedOrClosed={
                  virtualCard.isConsumed || virtualCard.isClosed
                }
              />
              {(isGuestCard || virtualCard.isPending) && (
                <VirtualCardAccountingBox
                  virtualCard={virtualCard}
                  openLeftMdUp={false}
                  openLeftSmDown
                  hasIntegrations={hasIntegrations}
                />
              )}
            </VerticalContentCollection>
            <VerticalContentCollection>
              {!isGuestCard && !virtualCard.isPending && (
                <VirtualCardAccountingBox
                  virtualCard={virtualCard}
                  openLeftMdUp
                  openLeftSmDown
                  hasIntegrations={hasIntegrations}
                />
              )}
              {isGuestCard && sender && recipient && (
                <AdditionalVirtualCardDetailsBox
                  virtualCard={virtualCard}
                  sender={sender}
                  recipient={recipient}
                  creditCard={creditCard}
                  pendingFinalApproval={isPendingFinalApproval}
                />
              )}
              <NotesBox
                notes={virtualCard.notes}
                copy={
                  <Trns
                    source={
                      "When you create or update a card, leave a memo about what it's for and your note will appear here."
                    }
                  />
                }
              />
            </VerticalContentCollection>
          </HorizontalContentCollection>
          {!virtualCard.isPending && (
            <ActivityBox
              defaultFiltersBody={virtualCardTransactionFilters}
              vcClearedTransactionAmount={
                vcClearedTransactionAmountFromState !== undefined
                  ? vcClearedTransactionAmountFromState[virtualCardId]
                  : undefined
              }
              showTransactionUser={false}
              userId={userId}
              timezone={virtualCard.timezone}
              currency={virtualCard.currency}
              virtualCard={virtualCard}
              clientHeight={clientHeight || 0}
            />
          )}
        </BackgroundPageContent>
      </div>
    </BackgroundPage>
  ) : getVirtualCardFailed ? (
    <NotFoundBackgroundPage />
  ) : (
    <LoadingBackgroundPage />
  )
}

export default VirtualCardDetail
